/**
 * Hex fallbacks for non-Tailwind contexts (e-mails, prompts, inline styles, SSR strings).
 * Keep in sync with `web-app/styles/jc-public-theme.css` (`:root` / `html.dark`).
 */
export const jcThemeHex = {
  light: {
    page: '#F8F5F0',
    surface: '#FFFFFF',
    elevated: '#F2EADF',
    elevatedHover: '#EDE3D4',
    border: '#DDD5C8',
    /** Default green logo tile, sync with --jc-logo-matte */
    logoMatte: '#3B5E45',
    /** Card background tile for transparent non-mono logos — sync with --jc-logo-surface. Fixed
     * white, does not flip in dark mode. */
    logoSurface: '#FFFFFF',
    /** Backdrop for mono black marks on alpha — sync with --jc-logo-mono-light. Fixed white, does
     * not flip in dark mode. */
    logoMonoLight: '#FFFFFF',
    /** Dark green tile for mono white marks on alpha (needs contrast) — sync with
     * --jc-logo-mono-dark. Fixed, does not flip in dark mode. */
    logoMonoDark: '#2A4533',
    /** @deprecated Prefer `logoMonoLight` / `logoMonoDark`. */
    logoMonoTan: '#FFFFFF',
    ink: '#16150F',
    inkSecondary: '#4A4438',
    muted: '#8C8474',
    onElevated: '#5A4A3A',
    accent: '#3B5E45',
    cta: '#3B5E45',
    ctaHover: '#2A4533',
    accentMuted: '#C8DDCE',
    accentSoft: '#5A8A68',
    gold: '#6E4F00',
    goldBg: '#F5EDD0',
    goldLine: '#B8860B',
    positive: '#3B5E45',
    negative: '#B3453D',
    cardBorder: 'rgba(22, 21, 15, 0.08)',
    filterLocationBg: '#A8C9C8',
    filterLocationText: '#1A2E2E',
    filterLocationBorder: '#4A7B79',
    filterSalaryBg: '#F5EDD0',
    filterSalaryText: '#6E4F00',
    filterSalaryBorder: '#B8860B',
    filterCompanyBg: '#D9A89A',
    filterCompanyText: '#3D1810',
    filterCompanyBorder: '#9A4F3C',
    filterJobBg: '#C8DDCE',
    filterJobText: '#2A4533',
    filterJobBorder: '#3B5E45',
    skeleton: '#D5CDC0',
  },
  dark: {
    page: '#170F07',
    surface: '#1F1509',
    elevated: '#2A1D0F',
    elevatedHover: '#342415',
    border: '#4A3520',
    logoMatte: '#2D4A38',
    logoSurface: '#FFFFFF',
    logoMonoLight: '#FFFFFF',
    logoMonoDark: '#2A4533',
    /** @deprecated Prefer `logoMonoLight` / `logoMonoDark`. */
    logoMonoTan: '#FFFFFF',
    ink: '#F2EADF',
    inkSecondary: '#C4A882',
    muted: '#9A917F',
    onElevated: '#C4A882',
    accent: '#98CFA8',
    cta: '#3B5E45',
    ctaHover: '#5A8A68',
    accentMuted: '#1A2E22',
    accentSoft: '#5A8A68',
    gold: '#D4A820',
    goldBg: '#2A1E08',
    goldLine: '#D4A820',
    positive: '#98CFA8',
    negative: '#E08277',
    cardBorder: 'rgba(74, 53, 32, 0.7)',
    filterLocationBg: '#1A3030',
    filterLocationText: '#9FD5D3',
    filterLocationBorder: '#4D8F8D',
    filterSalaryBg: '#2A1E08',
    filterSalaryText: '#EEC85A',
    filterSalaryBorder: '#D4A820',
    filterCompanyBg: '#3D221C',
    filterCompanyText: '#E8B0A0',
    filterCompanyBorder: '#B8624A',
    filterJobBg: '#1A2E22',
    filterJobText: '#98CFA8',
    filterJobBorder: '#5A8A68',
    skeleton: '#5C4535',
  },
} as const;

// <generated:next>
// Generated by scripts/design-system/generate-theme-hex.mjs from design-system/tokens/design-tokens.json.
// Do not edit by hand: run `npm run design:theme-hex`.
/** The next theme, light values. Same role names as `jcThemeHex.light` where the role exists, plus the new families. */
export const jcThemeHexNextLight = {
    page: '#f2f2ef',
    surface: '#ffffff',
    elevated: '#e7e7e2',
    elevatedHover: '#dcdcd6',
    border: '#dcdcd6',
    borderControl: '#86867e',
    cardBorder: 'rgba(23, 24, 27, 0.06)',
    ink: '#17181b',
    inkSecondary: '#4a4d54',
    tint: '#2f5a43',
    tintContainer: '#ffad1f',
    cta: '#ffad1f',
    onCta: '#1f1300',
    ctaHover: '#e69a12',
    action: '#17181b',
    onAction: '#ffffff',
    actionHover: '#2c2e33',
    accent: '#2f5a43',
    selected: '#2f5a43',
    onSelected: '#ffffff',
    accentMuted: '#e7e7e2',
    focus: '#17181b',
    onSolid: '#ffffff',
    live: '#2f5a43',
    positive: '#2f5a43',
    negative: '#17181b',
    filterLocationBg: '#e2ece4',
    filterLocationText: '#2f5a43',
    filterLocationBorder: '#2f5a43',
    filterSalaryBg: '#e7e7e2',
    filterSalaryText: '#17181b',
    filterSalaryBorder: '#4a4d54',
    filterCompanyBg: '#ffffff',
    filterCompanyText: '#17181b',
    filterCompanyBorder: '#86867e',
    filterJobBg: '#e2ece4',
    filterJobText: '#2f5a43',
    filterJobBorder: '#2f5a43',
    brandFallback: '#2f5a43',
    onBrandFallback: '#ffffff',
    forest: '#2f5a43',
    forestDeep: '#2f5a43',
    onForest: '#ffffff',
    onForestMuted: '#c6d3ca',
    forestSoft: '#e2ece4',
    footer: '#2f5a43',
    logoSurface: '#ffffff',
    onLogoSurface: '#4a4d54',
    logoMonoLight: '#ffffff',
    logoMonoDark: '#17181b',
    readoutBg: '#2f5a43',
    readoutTrack: 'rgba(255, 255, 255, 0.18)',
    readoutInk: '#ffffff',
    readoutMuted: '#c6d3ca',
    readoutFigure: '#ffad1f',
    readoutRemote: '#ffad1f',
    readoutHybrid: '#9cc7a8',
    readoutOnsite: '#c9c6bf',
    workRemote: '#ffad1f',
    workHybrid: '#2f5a43',
    workOnsite: '#8c8475',
    scrim: 'rgba(0, 0, 0, 0.32)',
    skeleton: '#e7e7e2',
    codeBlockBg: '#17181b',
    codeBlockText: '#e2ece4',
    chart1: '#15130f',
    chart2: '#ffad1f',
    chart3: '#2f5a43',
    chart4: '#9cc7a8',
    chart5: '#86867e',
    chart6: '#d6d3cb',
    chartContext: '#c9c6bf',
    chartGrid: '#ece8e0',
    chartSeq1: '#eef2ee',
    chartSeq2: '#c7dccd',
    chartSeq3: '#9cc7a8',
    chartSeq4: '#5e9474',
    chartSeq5: '#2f5a43',
    chartSeq6: '#1b3a2a',
    chartSeqOn1: '#17181b',
    chartSeqOn2: '#17181b',
    chartSeqOn3: '#17181b',
    chartSeqOn4: '#17181b',
    chartSeqOn5: '#ffffff',
    chartSeqOn6: '#ffffff',
    chartDivNeg2: '#4a4d54',
    chartDivNeg1: '#9a9da3',
    chartDivMid: '#f3f1ec',
    chartDivPos1: '#c8952f',
    chartDivPos2: '#6e4f00',
    logoMatte: '#2f5a43',
    logoMonoTan: '#ffffff',
    onElevated: '#4a4d54',
    accentSoft: '#2f5a43',
} as const;

/** The next theme, dark values. */
export const jcThemeHexNextDark = {
    page: '#262a27',
    surface: '#303531',
    elevated: '#3a403b',
    elevatedHover: '#454b46',
    border: '#454b46',
    borderControl: '#959d97',
    cardBorder: 'rgba(255, 255, 255, 0.06)',
    ink: '#f2f3f5',
    inkSecondary: '#c4c7cd',
    tint: '#8fd3a9',
    tintContainer: '#3d2a08',
    cta: '#ffad1f',
    onCta: '#1f1300',
    ctaHover: '#e69a12',
    action: '#f2f3f5',
    onAction: '#17181b',
    actionHover: '#ffffff',
    accent: '#8fd3a9',
    selected: '#cfe3d4',
    onSelected: '#0f1210',
    accentMuted: '#3a403b',
    focus: '#ffbe55',
    onSolid: '#17181b',
    live: '#6fbf8f',
    positive: '#8fd3a9',
    negative: '#f2f3f5',
    filterLocationBg: '#1c2a21',
    filterLocationText: '#8fd3a9',
    filterLocationBorder: '#8fd3a9',
    filterSalaryBg: '#3a403b',
    filterSalaryText: '#f2f3f5',
    filterSalaryBorder: '#c4c7cd',
    filterCompanyBg: '#303531',
    filterCompanyText: '#f2f3f5',
    filterCompanyBorder: '#959d97',
    filterJobBg: '#1c2a21',
    filterJobText: '#8fd3a9',
    filterJobBorder: '#8fd3a9',
    brandFallback: '#24503a',
    onBrandFallback: '#ffffff',
    forest: '#24503a',
    forestDeep: '#24503a',
    onForest: '#ffffff',
    onForestMuted: '#c6d3ca',
    forestSoft: '#1c2a21',
    footer: '#162319',
    logoSurface: '#ffffff',
    onLogoSurface: '#4a4d54',
    logoMonoLight: '#ffffff',
    logoMonoDark: '#17181b',
    readoutBg: '#24503a',
    readoutTrack: 'rgba(255, 255, 255, 0.18)',
    readoutInk: '#ffffff',
    readoutMuted: '#c6d3ca',
    readoutFigure: '#ffad1f',
    readoutRemote: '#ffad1f',
    readoutHybrid: '#9cc7a8',
    readoutOnsite: '#c9c6bf',
    workRemote: '#ffad1f',
    workHybrid: '#6fbf8f',
    workOnsite: '#77736b',
    scrim: 'rgba(0, 0, 0, 0.6)',
    skeleton: '#3a403b',
    codeBlockBg: '#1d201e',
    codeBlockText: '#e2ece4',
    chart1: '#f5f3ee',
    chart2: '#ffad1f',
    chart3: '#6fbf8f',
    chart4: '#2f6b4a',
    chart5: '#959d97',
    chart6: '#4f5550',
    chartContext: '#3a3a42',
    chartGrid: '#2e2e35',
    chartSeq1: '#1c2a21',
    chartSeq2: '#24503a',
    chartSeq3: '#3d7356',
    chartSeq4: '#5e9474',
    chartSeq5: '#8fbf9f',
    chartSeq6: '#c7dccd',
    chartSeqOn1: '#ffffff',
    chartSeqOn2: '#ffffff',
    chartSeqOn3: '#ffffff',
    chartSeqOn4: '#17181b',
    chartSeqOn5: '#17181b',
    chartSeqOn6: '#17181b',
    chartDivNeg2: '#c4c7cd',
    chartDivNeg1: '#6b6e75',
    chartDivMid: '#24242a',
    chartDivPos1: '#8a6a10',
    chartDivPos2: '#ffad1f',
    logoMatte: '#24503a',
    logoMonoTan: '#ffffff',
    onElevated: '#c4c7cd',
    accentSoft: '#8fd3a9',
} as const;

export const jcThemeHexNext = { light: jcThemeHexNextLight, dark: jcThemeHexNextDark } as const;
// </generated:next>
